import createFetchival from '@exodus/fetch/create-fetchival'
import {
  ASSET_TYPES,
  FILTER_LIST_MODES,
  ORDER_TYPES,
  PAYMENT_METHOD_TYPES,
  PROVIDERS,
} from '../constants.js'
import { filterByCountries, omitUndefinedValues } from '../utils.js'
import { ERROR_PAYMENT_METHOD_NOT_SUPPORTED } from '../errors.js'
import { BLOCKCHAIN_API_URL, ENABLED_ASSETS } from './constants.js'
import blockchainAdapter from './adapter.js'

export * from './constants.js'
export * from './utils.js'
export { default as adapter } from './adapter.js'

const blockchainProvider = (
  {
    apiKey,
    assets: assets_ = ENABLED_ASSETS,
    countries = {},
    countriesMode = FILTER_LIST_MODES.blacklist,
    enabled = true,
    url = BLOCKCHAIN_API_URL,
    fetch: globalFetch = fetch.bind(globalThis), // eslint-disable-line no-undef
    paymentMethodTypes,
  } = Object.create(null)
) => {
  if (!apiKey) {
    throw new Error('`apiKey` is required')
  }

  const fetchival = createFetchival({ fetch: globalFetch })
  const fetch_ = fetchival(url)

  const getAssets = async ({ countryCode, regionCode } = Object.create(null)) => {
    const availableAssets = []
    const providerAssets = await fetch_('partner-gateway/partner-api/v1/currencies', {
      headers: { 'x-public-api-key': apiKey },
    }).get()

    const isSellSupported = false

    for (const providerAsset of providerAssets) {
      if (providerAsset.type === 'FIAT') {
        availableAssets.push({
          isBuySupported: true,
          isSellSupported,
          maxBuyAmount: Number(providerAsset.maxBuyAmount),
          minBuyAmount: Number(providerAsset.minBuyAmount),
          name: providerAsset.code,
          type: ASSET_TYPES.fiat,
        })

        continue
      }

      const asset = blockchainAdapter.fromProviderToExodusAsset(
        providerAsset.code,
        providerAsset.network,
        assets_
      )

      if (!asset) {
        continue
      }

      const isBuySupported =
        countryCode !== 'US' ||
        (countryCode === 'US' &&
          providerAsset.isSupportedInUS &&
          (!regionCode || providerAsset.allowedUSStates.includes(regionCode)))

      availableAssets.push({
        isBuySupported,
        isSellSupported,
        maxBuyAmount: Number.MAX_SAFE_INTEGER,
        minBuyAmount: 0,
        name: asset.name,
        type: ASSET_TYPES.crypto,
      })
    }

    return availableAssets
  }

  const getCountries = async ({ countryCode, regionCode } = Object.create(null)) => {
    const availableCountries = []
    const { countries: providerCountries, usStates = [] } = await fetch_(
      'partner-gateway/partner-api/v1/regions',
      {
        headers: { 'x-public-api-key': apiKey },
      }
    ).get()

    const providerStates = new Set(
      usStates.filter((state) => state.isBuyAllowed).map((state) => state.alpha2)
    )

    for (const providerCountry of providerCountries) {
      const isCountrySupported = !filterByCountries(
        countries,
        providerCountry.alpha2 === countryCode ? [countryCode, regionCode] : providerCountry.alpha2,
        {
          mode: countriesMode,
          properties: [ORDER_TYPES.buy],
        }
      )

      const isStateSupported =
        providerCountry.alpha2 !== 'US' ||
        !countryCode ||
        !regionCode ||
        (countryCode === 'US' && providerStates.has(regionCode))

      const isBuySupported = isCountrySupported && isStateSupported && providerCountry.isBuyAllowed

      if (!isBuySupported) {
        continue
      }

      availableCountries.push({
        code: providerCountry.alpha2,
        isBuySupported,
        isSellSupported: false,
        name: providerCountry.name,
      })
    }

    return availableCountries
  }

  const getOrder = async (_, { serverOrder } = Object.create(null)) =>
    blockchainAdapter.fromProviderToExodusOrder(serverOrder)

  const getQuote = async ({
    countryCode,
    failIfNotAllowed = false,
    fromAmount,
    fromAsset,
    paymentMethod = PAYMENT_METHOD_TYPES.card,
    regionCode,
    toAsset,
    type = ORDER_TYPES.buy,
    walletAddress,
  }) => {
    if (type !== ORDER_TYPES.buy) {
      throw new Error('Only `buy` orders are accepted')
    }

    if (!fromAmount) {
      throw new Error('`fromAmount` is required')
    }

    const [providerToAsset] = blockchainAdapter.fromExodusToProviderAsset(toAsset, assets_)
    const providerPaymentMethod = blockchainAdapter.fromExodusToProviderPaymentMethod(
      paymentMethod,
      {
        assets: assets_,
        country: countryCode,
        currency: fromAsset,
        paymentMethodTypes,
      }
    )

    if (!providerPaymentMethod) {
      throw new Error(ERROR_PAYMENT_METHOD_NOT_SUPPORTED.message)
    }

    const quote = await fetch_('partner-gateway/partner-api/v1/quote/buy', {
      headers: { 'x-public-api-key': apiKey },
    }).get(
      omitUndefinedValues({
        baseCurrencyCode: providerToAsset,
        countryCode,
        failIfNotAllowed,
        paymentMethod: providerPaymentMethod,
        quoteCurrencyCode: fromAsset,
        quoteCurrencyAmount: fromAmount,
        usStateCode: countryCode === 'US' ? regionCode : undefined,
        walletAddress,
      })
    )

    return blockchainAdapter.fromProviderToExodusQuote({ ...quote, fromAsset, toAsset, type })
  }

  const getPaymentMethod = (paymentMethod, parameters = Object.create(null)) =>
    blockchainAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      ...parameters,
      assets: assets_,
      paymentMethodTypes,
    })

  return {
    name: PROVIDERS.blockchain,
    enabled,
    getAssets,
    getCountries,
    getPaymentMethod,
    getOrder,
    getQuote,
    orderTypes: [ORDER_TYPES.buy],
  }
}

export default blockchainProvider
